/*
 * Blog post resolver — single entry point for screens, SEO meta and the
 * build-time prerender matrix (Story 4.8, i18n-architecture §4.3).
 *
 * `postsFor(locale)` picks the per-locale file (fallback `SOURCE_LOCALE`).
 * `postSlugs(locale)` (consumed by react-router.config.ts + src/seo/meta.ts)
 * derives the prerender/hreflang set from per-locale RECORD PRESENCE — the
 * belgium-only articles live only in `nl.ts`, so they are nl-only everywhere by
 * construction (G5). Presentation helpers (dates, covers, tag tones) are
 * locale-INDEPENDENT and keyed by slug/tag over the full universe. Pure
 * data/functions only (NFR10): zero React.
 */
import { SOURCE_LOCALE, LIVE_LOCALES } from "../../i18n/locales";
// CMS overlay (DIT-264 / S8 → CMS), generated at build by scripts/fetch-cms-content.mjs.
// Static import so it resolves in both Vite and Node (vite.config build matrix);
// empty `{}` when there's no CMS → baked fallback.
import { CMS_ARTICLES } from "../_generated/cms-articles";
import { cdnImage } from "../../i18n/cmsImage";
import type { Post } from "./types";
import { POSTS_EN } from "./en";
import { POSTS_NL } from "./nl";
import { POSTS_FR_BE } from "./fr-be";
import { POSTS_ES } from "./es";
import { POSTS_DE } from "./de";
import { POSTS_IT } from "./it";
import { POSTS_PT } from "./pt";
import { POSTS_PL } from "./pl";
import { POSTS_RO } from "./ro";
import { POSTS_NO } from "./no";
import { POSTS_BG } from "./bg";
import { POSTS_TR } from "./tr";
import { POSTS_LT } from "./lt";

export type { Post, PostSection } from "./types";

/** Locale code → that locale's post set. `fr-be` added in Story 4.10 (E4.5). */
const REGISTRY: Record<string, Post[]> = {
  en: POSTS_EN,
  // Belgian English reuses the English (source) post set — en-be ships on the en
  // fallback across all content (see the industries barrel for the rationale).
  "en-be": POSTS_EN,
  nl: POSTS_NL,
  "fr-be": POSTS_FR_BE,
  es: POSTS_ES,
  de: POSTS_DE,
  it: POSTS_IT,
  pt: POSTS_PT,
  pl: POSTS_PL,
  ro: POSTS_RO,
  no: POSTS_NO,
  bg: POSTS_BG,
  tr: POSTS_TR,
  lt: POSTS_LT,
};

// Build-time guard (G5): a live locale with no post set would silently fall back
// to English (see `postsFor`) and ship wrong-language blog pages with HTTP 200.
// Fail the build loudly instead — promoting `fr-be` to `live` in E4.5 without
// adding it here throws at module load (prerender imports this barrel).
for (const { code } of LIVE_LOCALES) {
  if (!REGISTRY[code]) {
    throw new Error(
      `[content/posts] live locale "${code}" has no post set in REGISTRY — add it (or keep the locale 'draft').`,
    );
  }
}

/**
 * The canonical full post universe (newest-first), used only to derive
 * locale-stable publication dates. `nl` is the belgium superset (all 15 slugs,
 * including the 4 international ones), so a slug's date is identical under every
 * locale.
 */
/** CMS post set for a locale (when hydrated + non-empty), else undefined. */
const cmsPosts = (locale?: string): Post[] | undefined => {
  const c = locale ? (CMS_ARTICLES.posts[locale] as Post[] | undefined) : undefined;
  return c && c.length ? c : undefined;
};

const CANONICAL_POSTS: Post[] = cmsPosts("nl") ?? POSTS_NL;

/** Resolve the post set for a locale: CMS if hydrated, else baked (→ source). */
export const postsFor = (locale?: string): Post[] =>
  cmsPosts(locale) ?? ((locale && REGISTRY[locale]) || REGISTRY[SOURCE_LOCALE]);

/** slug → Post map for a locale (replaces the old global `POST_MAP`). */
export const postMapFor = (locale?: string): Record<string, Post> =>
  Object.fromEntries(postsFor(locale).map((p) => [p.slug, p]));

/**
 * Slug list that drives the prerender matrix (react-router.config.ts) and the
 * hreflang set (src/seo/meta.ts). Derived from per-locale presence.
 */
export const postSlugs = (locale?: string): string[] =>
  postsFor(locale).map((p) => p.slug);

// Placeholder publication dates (newest first), spaced ~6 days apart, until real
// dates are wired in. Keyed by slug over the canonical universe so /en and /nl
// show the same date for the same article. Powers the "Posted on" footer.
export const POST_DATE: Record<string, string> = Object.fromEntries(
  CANONICAL_POSTS.map((p, i) => {
    const d = new Date(Date.UTC(2025, 11, 1) - i * 6 * 86_400_000);
    return [p.slug, d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })];
  }),
);

export const TAG_TONE: Record<Post["tag"], "primary" | "accent" | "success"> = {
  Compliance: "accent",
  Mileage: "success",
  "E-invoicing": "primary",
  "GPS & privacy": "primary",
  "Cross-border": "primary",
};

// Real cover photo per theme (temporary stock until brand photography lands).
const UNSPLASH = "https://images.unsplash.com";
const COVER = "?w=1200&auto=format&fit=crop&q=80";
export const TAG_COVER: Record<Post["tag"], string> = {
  Compliance: `${UNSPLASH}/photo-1503387762-592deb58ef4e${COVER}`,        // construction site
  "E-invoicing": `${UNSPLASH}/photo-1554224155-6726b3ff858f${COVER}`,    // invoices + calculator
  Mileage: `${UNSPLASH}/photo-1449965408869-eaa3f722e40d${COVER}`,        // driving
  "GPS & privacy": `${UNSPLASH}/photo-1604357209793-fca5dca89f97${COVER}`, // phone GPS navigation
  "Cross-border": `${UNSPLASH}/photo-1519003722824-194d4455a60c${COVER}`, // truck on the highway
};

// Approved per-article covers, uploaded to Cloudinary under the post slug. Baked
// so each article shows its own photo everywhere (local dev never runs cms:fetch,
// and without these every post fell back to one shared tag photo). CMS overrides.
//
// NOTE: the Cloudinary public IDs (and therefore these keys) are the slug
// truncated to BAKED_COVER_KEY_LEN chars — long slugs are cut. `coverFor` must
// look up with the same truncation or long-slug articles miss their cover and
// fall back to the one shared tag photo (see DIT-386: "all pictures identical").
const BAKED_COVER_KEY_LEN = 60;
const BAKED_COVER: Record<string, string> = {
  "checkin-work-ciao-verplichtingen-fouten-en-tips-voor-kmo-s-i": "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784551012/blog/checkin-work-ciao-verplichtingen-fouten-en-tips-voor-kmo-s-i.jpg",
  "digitaal-in-en-uitchecken-verplicht-op-bouwwerven-vanaf-2025": "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784551013/blog/digitaal-in-en-uitchecken-verplicht-op-bouwwerven-vanaf-2025.jpg",
  "e-facturatie-in-belgie-peppol-verplicht-voor-alle-bedrijven-": "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784551014/blog/e-facturatie-in-belgie-peppol-verplicht-voor-alle-bedrijven-.jpg",
  "facturatie-van-verplaatsingen-naar-klanten-hoe-kilometers-co": "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784551016/blog/facturatie-van-verplaatsingen-naar-klanten-hoe-kilometers-co.jpg",
  "flashcontroles-in-de-belgische-bouwsector-zomer-2025-hoe-ver": "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784551018/blog/flashcontroles-in-de-belgische-bouwsector-zomer-2025-hoe-ver.jpg",
  "gids-voor-belgische-en-nederlandse-bedrijven-die-actief-zijn": "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784551019/blog/gids-voor-belgische-en-nederlandse-bedrijven-die-actief-zijn.jpg",
  "gps-tracking-field-workers-privacy-gdpr": "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784551021/blog/gps-tracking-field-workers-privacy-gdpr.jpg",
  "how-to-track-mileage-for-field-teams": "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784551023/blog/how-to-track-mileage-for-field-teams.jpg",
  "kilometervergoeding-in-belgie-regels-en-terugbetalingen-voor": "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784551038/blog/kilometervergoeding-in-belgie-regels-en-terugbetalingen-voor.jpg",
  "onderaannemers-in-schoonmaak-renovatie-en-nieuwbouw-in-belgi": "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784551040/blog/onderaannemers-in-schoonmaak-renovatie-en-nieuwbouw-in-belgi.jpg",
  "reducing-payroll-admin-for-field-service-companies": "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784551041/blog/reducing-payroll-admin-for-field-service-companies.jpg",
  "time-tracking-for-field-teams-complete-guide": "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784551042/blog/time-tracking-for-field-teams-complete-guide.jpg",
  "verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedri": "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784551043/blog/verplichte-arbeidstijdregistratie-in-belgie-wat-moeten-bedri.jpg",
  "verplichte-e-facturatie-b2b-via-peppol-in-belgie-vanaf-2026": "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784551044/blog/verplichte-e-facturatie-b2b-via-peppol-in-belgie-vanaf-2026.jpg",
  "wettelijk-en-ethisch-gebruik-van-gps-tracking-bij-mobiele-we": "https://res.cloudinary.com/lbx0aw1r/image/upload/v1784551045/blog/wettelijk-en-ethisch-gebruik-van-gps-tracking-bij-mobiele-we.jpg",
};

/** Cover photo for a post: CMS cover if set, else the baked per-slug cover
 *  (trying the full slug, then the truncated key the Cloudinary IDs actually use),
 *  else the tag default. Routed through `cdnImage` so Cloudinary originals are
 *  served resized — raw they are ~8000px wide and stall scrolling while they
 *  decode. Without the truncated fallback, every long-slug article dropped to the
 *  single per-tag photo, so same-tag lists (e.g. Wet 2027 related reading) all
 *  showed the identical cover — DIT-386. */
export const coverFor = (p: Post) =>
  cdnImage(
    p.cover ||
      BAKED_COVER[p.slug] ||
      BAKED_COVER[p.slug.slice(0, BAKED_COVER_KEY_LEN)] ||
      TAG_COVER[p.tag],
    1000,
  );

// Regression guard (DIT-386). The baked covers are keyed by the slug truncated to
// BAKED_COVER_KEY_LEN chars — a lossy scheme the repo has to mirror because the
// covers were baked to Cloudinary with truncated public IDs. Two ways a future
// article silently reintroduces the "identical covers" bug:
//   1. Collision — two slugs share the first BAKED_COVER_KEY_LEN chars, so they map
//      to one key and would swap covers. Unambiguously wrong → fail the build, the
//      same way the locale guard above does (prerender imports this barrel).
//   2. A long-slug article missing from BAKED_COVER drops to the shared per-tag
//      photo. Sometimes intended (cover not baked yet), so only warn, only in dev.
// Checked once over the canonical universe (nl = the full 15-slug superset).
{
  const claimedBy = new Map<string, string>();
  const uncovered: string[] = [];
  for (const p of CANONICAL_POSTS) {
    const key = BAKED_COVER[p.slug] ? p.slug : p.slug.slice(0, BAKED_COVER_KEY_LEN);
    if (!BAKED_COVER[key]) {
      uncovered.push(p.slug);
      continue;
    }
    const prior = claimedBy.get(key);
    if (prior && prior !== p.slug) {
      throw new Error(
        `[content/posts] baked-cover key collision: "${prior}" and "${p.slug}" both ` +
          `truncate to "${key}" and would share one cover. Give them distinct ` +
          `Cloudinary IDs or raise BAKED_COVER_KEY_LEN.`,
      );
    }
    claimedBy.set(key, p.slug);
  }
  const env = (import.meta as { env?: { DEV?: boolean } }).env;
  if (uncovered.length && env?.DEV) {
    console.warn(
      `[content/posts] ${uncovered.length} post(s) have no baked cover and fall back ` +
        `to the shared per-tag photo: ${uncovered.join(", ")}. Add a BAKED_COVER entry ` +
        `unless the tag default is intended.`,
    );
  }
}
